import * as THREE from "three";
import { useGlobalStore } from "../../../store";
import { setHasDoneOnce } from "../teethControls/TeethControls";
import {
  toggleIPRs,
  setActiveCameraIndex as setIPRActiveCameraIndex,
} from "@/helpers/ipr";
import { setActiveCameraIndex } from "@/helpers/clickManager";
import { measurementTool } from "../Mesh";
import { trackToolbarToggle } from "@/mixpanel/Mixpanel";

const { DEG2RAD } = THREE.MathUtils;

export const controllerActionStrings = {
  left: "left",
  right: "right",
  front: "front",
  top: "top",
  bottom: "bottom",
  upper: "upper",
  lower: "lower",
  "elastics-view": "elastics-view",
  "attachments-view": "attachments-view",
  "superImpose-view": "superImpose-view",
  "ipr-view": "ipr-view",
  "numbers-view": "numbers-view",
  "pontics-view": "pontics-view",
  "occlus-view": "occlus-view",
  "split-view": "split-view",
  "grid-view": "grid-view",
  "staging-view": "staging-view",
  "movement-view": "movement-view",
  "bolton-view": "bolton-view",
  "biteJump": "biteJump",
  "showOverbite": "showOverbite"
};

/**
 *
 * @param {keyof controllerActionStrings} action
 * @param {*} view
 * @param {*} setView
 * @param {*} setToggle
 * @param {*} toggle
 * @param {*} boltonInfo
 * @returns
 */
export const controller = (
  action,
  controlsRef,
  controlsRef2,
  view,
  setView,
  setToggle,
  toggle,
  boltonInfo,
) => {
  switch (action) {
    case "left":
      if (view === "left") return;
      controlsRef.reset(true);
      controlsRef2.reset(true);
      controlsRef?.rotate(90 * DEG2RAD, 0, true);
      controlsRef2?.rotate(90 * DEG2RAD, 0, true);
      setView(prev => "left");
      setToggle("showUpper", true);
      setToggle("showLower", true);
      break;
    case "right":
      if (view === "right") return;
      controlsRef.reset(true);
      controlsRef2.reset(true);
      controlsRef?.rotate(-90 * DEG2RAD, 0, true);
      controlsRef2?.rotate(-90 * DEG2RAD, 0, true);
      setView(prev => "right");
      setToggle("showUpper", true);
      setToggle("showLower", true);
      break;
    case "front":
      controlsRef.reset(true);
      controlsRef2.reset(true);
      setView(prev => "front");
      setToggle("showUpper", true);
      setToggle("showLower", true);
      break;
    case "top":
      if (view === "top") return;
      controlsRef.reset(true);
      controlsRef2.reset(true);
      controlsRef?.rotate(0, 90 * DEG2RAD, true);
      controlsRef2?.rotate(0, 90 * DEG2RAD, true);
      setView(prev => "top");
      setToggle("showUpper", true);
      setToggle("showLower", false);
      break;
    case "bottom":
      if (view === "bottom") return;
      controlsRef.reset(true);
      controlsRef2.reset(true);
      controlsRef?.rotate(0, -90 * DEG2RAD, true);
      controlsRef2?.rotate(0, -90 * DEG2RAD, true);
      setView(prev => "bottom");
      setToggle("showUpper", false);
      setToggle("showLower", true);
      break;
    case "upper":
      setToggle("showUpper", true);
      setToggle("showLower", false);
      break;
    case "lower":
      setToggle("showUpper", false);
      setToggle("showLower", true);
      break;
    case "attachments-view":
      setToggle("attachment");
      trackToolToggle("attachment", toggle);
      break;
    case "elastics-view":
      if (toggle.show3DControls) return;
      setToggle("elastics");
      trackToolToggle("elastics", toggle);
      break;
    case "superImpose-view":
      if (toggle.split || toggle.occ || toggle.compareModifications) return;
      setToggle("superImpose");
      trackToolToggle("superImpose", toggle);
      break;
    case "ipr-view":
      toggleIPRs(!toggle.ipr);
      setToggle("ipr");
      trackToolToggle("ipr", toggle);
      break;
    case "numbers-view":
      setToggle("numbering");
      trackToolToggle("numbering", toggle);
      break;
    case "pontics-view":
      setToggle("pontics");
      trackToolToggle("pontics", toggle);
      break;
    case "occlus-view":
      if (toggle.superImpose) return;
      setToggle("occ");
      trackToolToggle("occ", toggle);
      break;
    case "split-view":
      if (toggle.superImpose || toggle.show3DControls) return;
      controlsRef.reset(true);
      controlsRef2.reset(true);
      setToggle("split");
      measurementTool.isSplit = !toggle.split;
      trackToolToggle("split", toggle);
      setHasDoneOnce(false);
      break;
    case "grid-view":
      // if (toggle.show3DControls) return;
      setToggle("grid");
      trackToolToggle("grid", toggle);
      setActiveCameraIndex(toggle.grid ? 2 : 0);
      setIPRActiveCameraIndex(toggle.grid ? 2 : 0);
      break;
    case "staging-view":
      setToggle("showStaging");
      trackToolToggle("showStaging", toggle);
      break;
    case "movement-view":
      setToggle("showMovement");
      trackToolToggle("showMovement", toggle);
      break;
    case "bolton-view":
      if (boltonInfo === null) return;
      setToggle("showBolton");
      trackToolToggle("showBolton", toggle);
      break;
    case "records-view":
      setToggle("showRecords");
      trackToolToggle("showRecords", toggle);
      break;
    case "ruler":
      setToggle("showRuler");
      trackToolToggle("showRuler", toggle);
      measurementTool.toggleMeasurement();
      break;
    case "biteJump":
      if (toggle.show3DControls && !toggle.hasBiteJump) return;
      setToggle("biteJump");
      trackToolToggle("biteJump", toggle);
      break;
    case "showOverbite":
      if (toggle.show3DControls) return;
      setToggle("showOverbite");
      trackToolToggle("showOverbite", toggle);
      break;
    default:
      controlsRef.reset(true);
      controlsRef2.reset(true);
  }
};

const trackToolToggle = (key, toggle) => {
  const keyLabel = getToolLabel(key);

  // Map all keys
  const mappedKeys = Object.entries(toggle).reduce((acc, [k, v]) => {
    acc[getToolLabel(k)] = v || false;
    return acc;
  }, {});

  // Toggle the selected key
  mappedKeys[keyLabel] = !toggle[key] || false;

  const eventName = toggle[key] ? `${keyLabel} Off` : `${keyLabel} On`;
  trackToolbarToggle(eventName, mappedKeys);
};

const getToolLabel = (key) => {
  const ToolLabels = {
    attachment: "Attachments",
    ipr: "IPR",
    elastics: "Elastics",
    pontics: "Pontics",
    superImpose: "Superimpose",
    occ: "Occlusion",
    grid: "Grid",
    split: "Compare",
    numbering: "Number",
    biteJump: "Bite Jump",
    showOverbite: "OBOJ",
    showBolton: "Bolton",
    showStaging: "Staging",
    showMovement: "MVMT",
    showRecords: "Records",
    showRuler: "Ruler",
  };

  return ToolLabels[key] || "";
};

